import { useRef, useEffect, useState } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Plus, X, ChevronRight, ChevronDown, Trash2, Edit2, FileUp, Download, Package, Box, FolderOpen, Folder, Search, ArrowRight, Check, FileSpreadsheet, Move } from 'lucide-react';

export default function CategoriesPage() {
    const { user } = useAuth();
    const canEdit = Array.isArray(user?.permissions) && user.permissions.includes('categorias_editar');
    const canDelete = Array.isArray(user?.permissions) && user.permissions.includes('categorias_eliminar');
    const canViewProducts = Array.isArray(user?.permissions) && user.permissions.includes('productos_ver');
    const canEditProducts = Array.isArray(user?.permissions) && user.permissions.includes('productos_editar');
    const fileInputRef = useRef(null);
    const [categories, setCategories] = useState([]);
    const [orphanProducts, setOrphanProducts] = useState([]);
    const [expandedCats, setExpandedCats] = useState({});
    const [expandedSubs, setExpandedSubs] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('category');
    const [parentId, setParentId] = useState(null);
    const [formData, setFormData] = useState({ nombre: '', descripcion: '' });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Move modals
    const [showMoveSubModal, setShowMoveSubModal] = useState(false);
    const [showMoveProductModal, setShowMoveProductModal] = useState(false);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [targetCategoryId, setTargetCategoryId] = useState('');
    const [targetSubcategoryId, setTargetSubcategoryId] = useState('');
    const [bulkMoveMode, setBulkMoveMode] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_URL}/api/categories/full`);
            const data = await res.json();
            setCategories(data.categories || []);
            setOrphanProducts(data.orphanProducts || []);
        } catch (err) {
            console.error(err);
            try {
                const res = await apiFetch(`${API_URL}/api/categories`);
                const data = await res.json();
                setCategories(data.map(c => ({ ...c, subcategories: c.subcategories?.map(s => ({ ...s, products: [] })) || [] })));
            } catch (e) { console.error(e); }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const res = await apiFetch(`${API_URL}/api/categories/template`);
            if (!res.ok) throw new Error('Error al descargar');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'plantilla_categorias.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(err.message || 'Error al descargar plantilla');
        }
    };

    const handleExportProducts = async () => {
        try {
            const res = await apiFetch(`${API_URL}/api/products/export`);
            if (!res.ok) throw new Error('Error al exportar');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `productos_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(err.message || 'Error al exportar productos');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            const res = await apiFetch(`${API_URL}/api/categories/bulk`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();

            if (res.ok) {
                alert(`Carga completada: ${data.success} éxitos, ${data.failed} fallos.`);
                fetchCategories();
            } else {
                alert(`Error: ${data.error}`);
            }
        } catch (err) {
            console.error(err);
            alert('Error al subir el archivo');
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const toggleCat = (id) => setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));
    const toggleSub = (id) => setExpandedSubs(prev => ({ ...prev, [id]: !prev[id] }));

    const expandAll = () => {
        const cats = {};
        const subs = {};
        categories.forEach(c => {
            cats[c.id] = true;
            c.subcategories?.forEach(s => { subs[s.id] = true; });
        });
        setExpandedCats(cats);
        setExpandedSubs(subs);
    };

    const collapseAll = () => {
        setExpandedCats({});
        setExpandedSubs({});
    };

    const openModal = (type, parent = null) => {
        setModalType(type);
        setParentId(parent);
        setFormData({ nombre: '', descripcion: '' });
        setShowModal(true);
        setEditingItem(null);
    };

    const openEditModal = (type, item) => {
        setModalType(type);
        setParentId(type === 'subcategory' ? item.categoria_id : null);
        setFormData({ nombre: item.nombre, descripcion: item.descripcion || '' });
        setEditingItem(item);
        setShowModal(true);
    };

    const [editingItem, setEditingItem] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (editingItem) {
            // Update existing
            const endpoint = modalType === 'category'
                ? `${API_URL}/api/categories/${editingItem.id}`
                : `${API_URL}/api/subcategories/${editingItem.id}`;

            const body = modalType === 'category'
                ? formData
                : { nombre: formData.nombre, categoria_id: parentId };

            try {
                const res = await apiFetch(endpoint, {
                    method: 'PUT',
                    body: JSON.stringify(body)
                });
                if (res.ok) {
                    setShowModal(false);
                    setEditingItem(null);
                    fetchCategories();
                } else {
                    alert('Error al actualizar');
                }
            } catch (err) {
                console.error(err);
                alert('Error al actualizar');
            }
        } else {
            // Create new
            const endpoint = modalType === 'category'
                ? `${API_URL}/api/categories`
                : `${API_URL}/api/subcategories`;

            const body = modalType === 'category'
                ? formData
                : { nombre: formData.nombre, categoria_id: parentId };

            try {
                const res = await apiFetch(endpoint, {
                    method: 'POST',
                    body: JSON.stringify(body)
                });
                if (res.ok) {
                    setShowModal(false);
                    fetchCategories();
                } else {
                    alert('Error al crear');
                }
            } catch (err) {
                console.error(err);
                alert('Error al crear');
            }
        }
    };

    const handleDelete = async (type, id) => {
        if (!confirm('¿Estás seguro de eliminar esto?')) return;
        const endpoint = type === 'category'
            ? `${API_URL}/api/categories/${id}`
            : `${API_URL}/api/subcategories/${id}`;

        try {
            await apiFetch(endpoint, { method: 'DELETE' });
            fetchCategories();
        } catch (err) {
            console.error(err);
        }
    };

    // Move subcategory to another category
    const openMoveSubModal = (subcategory, currentCatId) => {
        setSelectedSubcategory({ ...subcategory, currentCatId });
        setTargetCategoryId('');
        setShowMoveSubModal(true);
    };

    const handleMoveSubcategory = async () => {
        if (!targetCategoryId || !selectedSubcategory) return;
        
        try {
            const res = await apiFetch(`${API_URL}/api/subcategories/${selectedSubcategory.id}/move`, {
                method: 'PUT',
                body: JSON.stringify({ categoria_id: parseInt(targetCategoryId) })
            });
            
            if (res.ok) {
                setShowMoveSubModal(false);
                setSelectedSubcategory(null);
                fetchCategories();
            } else {
                alert('Error al mover la subcategoría');
            }
        } catch (err) {
            console.error(err);
            alert('Error al mover la subcategoría');
        }
    };

    // Move product to another subcategory
    const openMoveProductModal = (product, currentSubId = null) => {
        setSelectedProduct({ ...product, currentSubId });
        setSelectedProducts([product.id]);
        setTargetCategoryId('');
        setTargetSubcategoryId('');
        setBulkMoveMode(false);
        setShowMoveProductModal(true);
    };

    const openBulkMoveModal = (products, currentSubId) => {
        setSelectedProducts(products.map(p => p.id));
        setSelectedProduct({ currentSubId });
        setTargetCategoryId('');
        setTargetSubcategoryId('');
        setBulkMoveMode(true);
        setShowMoveProductModal(true);
    };

    const handleMoveProduct = async () => {
        if (!targetSubcategoryId) return;
        
        try {
            if (bulkMoveMode && selectedProducts.length > 1) {
                const res = await apiFetch(`${API_URL}/api/products/move-bulk`, {
                    method: 'PUT',
                    body: JSON.stringify({ 
                        product_ids: selectedProducts,
                        sub_categoria_id: parseInt(targetSubcategoryId) 
                    })
                });
                
                if (res.ok) {
                    setShowMoveProductModal(false);
                    setSelectedProducts([]);
                    fetchCategories();
                } else {
                    alert('Error al mover los productos');
                }
            } else {
                const res = await apiFetch(`${API_URL}/api/products/${selectedProducts[0]}/move`, {
                    method: 'PUT',
                    body: JSON.stringify({ sub_categoria_id: parseInt(targetSubcategoryId) })
                });
                
                if (res.ok) {
                    setShowMoveProductModal(false);
                    setSelectedProduct(null);
                    fetchCategories();
                } else {
                    alert('Error al mover el producto');
                }
            }
        } catch (err) {
            console.error(err);
            alert('Error al mover el producto');
        }
    };

    // Toggle product selection for bulk move
    const toggleProductSelection = (productId) => {
        setSelectedProducts(prev => 
            prev.includes(productId) 
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const formatCurrency = (value) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);

    // Get subcategories for target category (for product move)
    const getTargetSubcategories = () => {
        if (!targetCategoryId) return [];
        const cat = categories.find(c => c.id === parseInt(targetCategoryId));
        return cat?.subcategories || [];
    };

    // Filter by search
    const filteredCategories = categories.map(cat => {
        if (!searchTerm) return cat;
        
        const matchesCat = cat.nombre?.toLowerCase().includes(searchTerm.toLowerCase());
        const filteredSubs = cat.subcategories?.map(sub => {
            const matchesSub = sub.nombre?.toLowerCase().includes(searchTerm.toLowerCase());
            const filteredProducts = sub.products?.filter(p => 
                p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.codigo_barra?.includes(searchTerm)
            ) || [];
            
            if (matchesSub || filteredProducts.length > 0) {
                return { ...sub, products: matchesSub ? sub.products : filteredProducts };
            }
            return null;
        }).filter(Boolean) || [];

        if (matchesCat || filteredSubs.length > 0) {
            return { ...cat, subcategories: matchesCat ? cat.subcategories : filteredSubs };
        }
        return null;
    }).filter(Boolean);

    // Count totals
    const totalCats = categories.length;
    const totalSubs = categories.reduce((acc, c) => acc + (c.subcategories?.length || 0), 0);
    const totalProducts = categories.reduce((acc, c) => 
        acc + (c.subcategories?.reduce((a, s) => a + (s.products?.length || 0), 0) || 0), 0);

    return (
        <div className="page categories-page p-6">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <FolderOpen className="text-indigo-600" size={28} />
                        Gestión de Categorías
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {totalCats} categorías • {totalSubs} subcategorías • {totalProducts} productos
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {canViewProducts && (
                        <button onClick={handleExportProducts} className="btn-secondary flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded text-green-700 hover:bg-green-100 text-sm">
                            <FileSpreadsheet size={16} /> Exportar Productos
                        </button>
                    )}
                    {canEdit && (
                        <>
                            <button onClick={handleDownloadTemplate} className="btn-secondary flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm">
                                <Download size={16} /> Plantilla
                            </button>
                            <button onClick={() => fileInputRef.current?.click()} className="btn-secondary flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded text-indigo-700 hover:bg-indigo-100 text-sm">
                                <FileUp size={16} /> Importar
                            </button>
                            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".xlsx,.xls,.csv" onChange={handleFileUpload} />
                            <button className="btn-primary flex items-center gap-2" onClick={() => openModal('category')}>
                                <Plus size={18} /> Nueva Categoría
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Search and Controls */}
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar categoría, subcategoría o producto..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-14 pr-4 py-2 border rounded-lg"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={expandAll} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded">
                            Expandir todo
                        </button>
                        <button onClick={collapseAll} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded">
                            Colapsar todo
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-12 text-gray-500">Cargando...</div>
            ) : (
                <div className="space-y-4">
                    {filteredCategories.map(cat => (
                        <div key={cat.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                            {/* Category Header */}
                            <div 
                                className="flex items-center justify-between p-4 bg-indigo-50 cursor-pointer hover:bg-indigo-100 transition-colors"
                                onClick={() => toggleCat(cat.id)}
                            >
                                <div className="flex items-center gap-3">
                                    {expandedCats[cat.id] ? 
                                        <ChevronDown className="text-indigo-600" size={22} /> : 
                                        <ChevronRight className="text-gray-400" size={22} />
                                    }
                                    <Folder className={expandedCats[cat.id] ? "text-indigo-600" : "text-gray-400"} size={22} />
                                    <div>
                                        <span className="font-bold text-gray-800 text-lg">{cat.nombre}</span>
                                        <span className="ml-3 text-sm text-gray-500">
                                            {cat.subcategories?.length || 0} subcategorías
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                                    {canEdit && (
                                        <button
                                            className="px-3 py-1 text-sm bg-indigo-500 text-gray-900 rounded hover:bg-indigo-700"
                                            onClick={() => openModal('subcategory', cat.id)}
                                        >
                                            + Subcategoría
                                        </button>
                                    )}
                                    {canEdit && (
                                        <button
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openEditModal('category', cat);
                                            }}
                                            title="Editar categoría"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            onClick={() => handleDelete('category', cat.id)}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Subcategories */}
                            {expandedCats[cat.id] && (
                                <div className="border-t">
                                    {cat.subcategories && cat.subcategories.length > 0 ? (
                                        cat.subcategories.map(sub => (
                                            <div key={sub.id} className="border-b last:border-b-0">
                                                {/* Subcategory Header */}
                                                <div 
                                                    className="flex items-center justify-between px-6 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                                                    onClick={() => toggleSub(sub.id)}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        {expandedSubs[sub.id] ? 
                                                            <ChevronDown className="text-gray-500" size={18} /> : 
                                                            <ChevronRight className="text-gray-400" size={18} />
                                                        }
                                                        <span className="font-semibold text-gray-700">{sub.nombre}</span>
                                                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                                                            {sub.products?.length || 0} productos
                                                        </span>
                                                    </div>
                                                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                                        {canEdit && (
                                                            <>
                                                                <button
                                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                                                    onClick={() => openEditModal('subcategory', sub)}
                                                                    title="Editar subcategoría"
                                                                >
                                                                    <Edit2 size={16} />
                                                                </button>
                                                                <button
                                                                    className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"
                                                                    onClick={() => openMoveSubModal(sub, cat.id)}
                                                                    title="Mover a otra categoría"
                                                                >
                                                                    <Move size={16} />
                                                                </button>
                                                            </>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                                onClick={() => handleDelete('subcategory', sub.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Products */}
                                                {expandedSubs[sub.id] && sub.products && sub.products.length > 0 && (
                                                    <div className="bg-white">
                                                        <div className="px-6 py-2 bg-gray-100 flex justify-between items-center">
                                                            <span className="text-xs text-gray-500">Productos en esta subcategoría</span>
                                                            {sub.products.length > 1 && canEditProducts && (
                                                                <button
                                                                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                                    onClick={() => openBulkMoveModal(sub.products, sub.id)}
                                                                >
                                                                    <Move size={12} /> Mover varios
                                                                </button>
                                                            )}
                                                        </div>
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-gray-100 text-gray-600">
                                                                <tr>
                                                                    <th className="px-6 py-2 text-left font-medium">Producto</th>
                                                                    <th className="px-4 py-2 text-left font-medium">Código</th>
                                                                    <th className="px-4 py-2 text-center font-medium">
                                                                        <div className="flex items-center justify-center gap-1">
                                                                            <Box size={14} /> Stock
                                                                        </div>
                                                                    </th>
                                                                    <th className="px-4 py-2 text-right font-medium">Precio</th>
                                                                    <th className="px-4 py-2 text-center font-medium">Acciones</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {sub.products.map(prod => (
                                                                    <tr key={prod.id} className="border-t hover:bg-gray-50">
                                                                        <td className="px-6 py-2">
                                                                            <div className="flex items-center gap-2">
                                                                                <Package size={14} className="text-gray-400" />
                                                                                <span className="font-medium">{prod.nombre}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-2 text-gray-500 font-mono text-xs">
                                                                            {prod.codigo_barra || '-'}
                                                                        </td>
                                                                        <td className="px-4 py-2 text-center">
                                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                                                                prod.stock_actual <= 0 ? 'bg-red-100 text-red-700' :
                                                                                prod.stock_actual <= 5 ? 'bg-yellow-100 text-yellow-700' :
                                                                                'bg-green-100 text-green-700'
                                                                            }`}>
                                                                                {prod.stock_actual || 0}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-4 py-2 text-right font-semibold text-green-600">
                                                                            {formatCurrency(prod.valor_producto)}
                                                                        </td>
                                                                        <td className="px-4 py-2 text-center">
                                                                            {canEditProducts && (
                                                                                <button
                                                                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                                                    onClick={() => openMoveProductModal(prod, sub.id)}
                                                                                    title="Mover a otra subcategoría"
                                                                                >
                                                                                    <ArrowRight size={16} />
                                                                                </button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {expandedSubs[sub.id] && (!sub.products || sub.products.length === 0) && (
                                                    <div className="px-8 py-4 text-gray-400 text-sm italic bg-gray-50">
                                                        No hay productos en esta subcategoría
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-6 py-4 text-gray-400 text-sm italic">
                                            No hay subcategorías. Haz clic en "+ Subcategoría" para agregar una.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    {filteredCategories.length === 0 && !loading && (
                        <div className="text-center py-12 bg-white rounded-lg border border-dashed">
                            <FolderOpen size={48} className="mx-auto text-gray-300 mb-4" />
                            <p className="text-gray-500">
                                {searchTerm ? 'No se encontraron resultados' : 'No hay categorías creadas'}
                            </p>
                            {!searchTerm && (
                                <button 
                                    onClick={() => openModal('category')}
                                    className="mt-4 btn-primary"
                                >
                                    Crear primera categoría
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Create Category/Subcategory Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">
                                {editingItem 
                                    ? (modalType === 'category' ? 'Editar Categoría' : 'Editar Subcategoría')
                                    : (modalType === 'category' ? 'Nueva Categoría' : 'Nueva Subcategoría')
                                }
                            </h2>
                            <button onClick={() => {
                                setShowModal(false);
                                setEditingItem(null);
                            }} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Nombre</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    required
                                    value={formData.nombre}
                                    onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                    autoFocus
                                    placeholder="Ej. Bebidas, Lácteos..."
                                />
                            </div>
                            {modalType === 'category' && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Descripción</label>
                                    <textarea
                                        className="w-full border rounded-lg p-2"
                                        rows="3"
                                        value={formData.descripcion}
                                        onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
                                        placeholder="Descripción breve de la categoría"
                                    />
                                </div>
                            )}
                            <div className="flex gap-3 mt-6">
                                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 border rounded-lg hover:bg-gray-50">
                                    Cancelar
                                </button>
                                <button type="submit" className="flex-1 py-2 bg-indigo-500 text-gray-900 rounded-lg hover:bg-indigo-700">
                                    {editingItem ? 'Guardar Cambios' : (modalType === 'category' ? 'Crear Categoría' : 'Crear Subcategoría')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Move Subcategory Modal */}
            {showMoveSubModal && selectedSubcategory && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <Move className="text-blue-600" size={22} />
                                Mover Subcategoría
                            </h2>
                            <button onClick={() => setShowMoveSubModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500">Subcategoría a mover:</p>
                            <p className="font-semibold text-gray-800">{selectedSubcategory.nombre}</p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Seleccionar categoría destino:</label>
                            <select
                                className="w-full p-2 border rounded-lg"
                                value={targetCategoryId}
                                onChange={e => setTargetCategoryId(e.target.value)}
                            >
                                <option value="">-- Seleccionar categoría --</option>
                                {categories
                                    .filter(c => c.id !== selectedSubcategory.currentCatId)
                                    .map(c => (
                                        <option key={c.id} value={c.id}>{c.nombre}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button 
                                type="button" 
                                onClick={() => setShowMoveSubModal(false)} 
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="button"
                                onClick={handleMoveSubcategory}
                                disabled={!targetCategoryId}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <Check size={18} /> Mover
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Move Product Modal */}
            {showMoveProductModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <ArrowRight className="text-blue-600" size={22} />
                                {bulkMoveMode ? `Mover ${selectedProducts.length} Productos` : 'Mover Producto'}
                            </h2>
                            <button onClick={() => setShowMoveProductModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        
                        {!bulkMoveMode && selectedProduct && (
                            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm text-gray-500">Producto a mover:</p>
                                <p className="font-semibold text-gray-800">{selectedProduct.nombre}</p>
                            </div>
                        )}

                        {bulkMoveMode && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-600">Se moverán {selectedProducts.length} productos seleccionados</p>
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">1. Seleccionar categoría:</label>
                            <select
                                className="w-full p-2 border rounded-lg"
                                value={targetCategoryId}
                                onChange={e => {
                                    setTargetCategoryId(e.target.value);
                                    setTargetSubcategoryId('');
                                }}
                            >
                                <option value="">-- Seleccionar categoría --</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.nombre}</option>
                                ))}
                            </select>
                        </div>

                        {targetCategoryId && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">2. Seleccionar subcategoría:</label>
                                <select
                                    className="w-full p-2 border rounded-lg"
                                    value={targetSubcategoryId}
                                    onChange={e => setTargetSubcategoryId(e.target.value)}
                                >
                                    <option value="">-- Seleccionar subcategoría --</option>
                                    {getTargetSubcategories().map(s => (
                                        <option key={s.id} value={s.id}>{s.nombre}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="flex gap-3 mt-6">
                            <button 
                                type="button" 
                                onClick={() => setShowMoveProductModal(false)} 
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="button"
                                onClick={handleMoveProduct}
                                disabled={!targetSubcategoryId}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <Check size={18} /> Mover
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
